/**
 * PATH: packages/ui/src/hooks/useNavCustomization.ts
 * FILE_ID: UI-NAV-HOOK-001
 * PURPOSE: Per-hub navigation customisation state + actions for the
 *          shared `@quotech/ui` Sidebar's right-click menu.
 * WHY: When the user right-clicks a nav item and picks "Pin" / "Move
 *      Up" / etc., something has to:
 *        1. Mutate the user's customisation blob,
 *        2. Persist to localStorage (snappy reload + offline),
 *        3. Sync to backend `/api/settings/navigation/` (cross-device).
 *      Doing this in every host's App.tsx invites drift. One hook,
 *      one storage key, one API endpoint, one resolver shape.
 *
 * FLOW:
 *   ┌────────────────┐  ┌──────────────────┐  ┌─────────────────┐
 *   │ Load on mount  │→ │ User right-clicks│→ │ Mutate + persist│
 *   │ (LS + backend) │  │ Pin/Move/etc.    │  │ (LS + backend)  │
 *   └────────────────┘  └──────────────────┘  └─────────────────┘
 *
 * DEPENDENCIES:
 *   - localStorage 'quotech_nav_customization' — same key as
 *     NavigationCustomization modal so both UIs stay in sync.
 *   - Optional `apiClient` for cross-device sync. When omitted the
 *     hook is localStorage-only (offline-friendly).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type {
  HubId,
  NavItem,
  PinnedNavItem,
} from '../components/types'
import type {
  NavContextResolution,
  NavContextResolver,
  NavSection,
} from '../components/sidebar/types'
import { trackNavClick as recordNavClick } from '../lib/navTracking'
import {
  NAV_CUSTOMIZATION_CHANGED_EVENT,
  readNavCustomization,
  writeNavCustomization,
  type NavCustomizationState,
} from './navCustomizationStorage'
import {
  partitionHidden, reorderItem, resolveOrderedNavItems, toggleHidden,
} from './navOrder'

// Re-export the storage/order helpers so existing imports
// (`from './useNavCustomization'`) and the hooks barrel keep working
// unchanged after the 500-line split (UI-NAV-ORDER-001 / UI-NAV-STORE-001).
export {
  applyNavItemOrder, partitionHidden, reorderItem, resolveOrderedNavItems, toggleHidden,
} from './navOrder'
export { NAV_CUSTOMIZATION_CHANGED_EVENT } from './navCustomizationStorage'

const SAVE_DEBOUNCE_MS = 600

/** Minimal axios-like client signature so any hub can pass its existing client. */
export interface NavCustomizationApiClient {
  get: (url: string) => Promise<{ data?: unknown } | unknown>
  put: (url: string, body: unknown) => Promise<unknown>
}

export interface UseNavCustomizationOptions {
  /** The hub these reorder operations apply to. Right-click "Move Up"
   *  on a Jobs hub nav item lives in `customization.navItemOrder['jobs']`. */
  hubId: string
  /** The hub's config-defined nav items. Used to compute Move-Up/Down
   *  bounds AND to materialise PinnedNavItem objects from the bare
   *  `{id, sourceHub, label, href}` shape we store in localStorage. */
  navItems: NavItem[]
  /** Optional axios-like client. When provided, the hook mirrors the
   *  in-memory state to `/api/settings/navigation/` on every change
   *  (debounced). When omitted, localStorage-only. */
  apiClient?: NavCustomizationApiClient
  /** Optional organisation id to scope the backend request. */
  organisationId?: string
  /** Called when the user picks "Customise…" from the menu — typically
   *  opens the settings modal on the Navigation tab. */
  onOpenCustomize?: () => void
  /**
   * Explicit override for the usage-driven "Smart ordering" gate. When
   * provided it WINS over the stored `aiAutoOrder` flag (useful for
   * tests, Storybook, or a host that owns the preference itself). When
   * omitted, the hook reads `aiAutoOrder` from the shared
   * `quotech_nav_customization` blob (default off — opt-in).
   */
  autoOrderByUsage?: boolean
  /**
   * Ordering strategy when smart-ordering is active. 'usage' (default) =
   * click-count weighted (SAM-style); 'recency' = the most-recently-clicked
   * item floats to the front. The master rail uses 'recency' so a click
   * deterministically sends an item to the top (operator 2026-06-19).
   */
  navOrderStrategy?: 'usage' | 'recency'
  /**
   * How many leading items to anchor in place when smart-ordering is
   * active. Default 1 (anchors the hub overview / Dashboard). Pass 0 so the
   * clicked item can reach the ABSOLUTE top (index 0).
   */
  preserveFirstCount?: number
}

export interface UseNavCustomizationReturn {
  /** Pass to `<DashboardLayout pinnedItems={...}>`. */
  pinnedItems: PinnedNavItem[]
  /** Pass to `<DashboardLayout resolveNavContext={...}>`. */
  resolveNavContext: NavContextResolver
  /**
   * The hub's nav items in the user's preferred order, with HIDDEN items
   * removed. Pass to `<DashboardLayout navItems={...}>` in place of the
   * raw config `navItems` so "Move Up / Down" AND "Hide" change what the
   * user sees.
   */
  orderedNavItems: NavItem[]
  /**
   * The nav items the user has hidden (right-click → "Hide from
   * sidebar"), in config order. Pass to `<DashboardLayout
   * hiddenItems={...}>` so the Sidebar can render the "Hidden (N)"
   * restore tray. Empty when nothing is hidden.
   */
  hiddenItems: NavItem[]
  /** Restore a hidden nav item to the rail (used by the restore tray). */
  unhideItem: (itemId: string) => void
  /**
   * Record a left-click / tap on a main-nav item so the usage-driven
   * "Smart ordering" engine learns what the operator reaches for. Bound
   * to this hook's `hubId` — the host just passes the item id. Wiring
   * it is opt-in: a host that never calls it simply records no usage
   * (and `orderedNavItems` stays in manual order even with the flag on).
   */
  trackNavClick: (itemId: string) => void
  /** Whether usage-driven ordering is currently active (flag resolved). */
  autoOrderEnabled: boolean
  /** Loading state — true while the initial backend GET is in flight. */
  isLoading: boolean
}

export function useNavCustomization(
  opts: UseNavCustomizationOptions,
): UseNavCustomizationReturn {
  const {
    hubId, navItems, apiClient, organisationId, onOpenCustomize, autoOrderByUsage,
    navOrderStrategy, preserveFirstCount,
  } = opts

  const [state, setState] = useState<NavCustomizationState>(() => readNavCustomization())
  const [isLoading, setIsLoading] = useState<boolean>(Boolean(apiClient))
  // Bumped on every tracked nav click so `orderedNavItems` re-sorts
  // live as the operator navigates (mirrors useSortedByActionUsage).
  const [usageEpoch, setUsageEpoch] = useState(0)
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // ── Hydrate from backend on mount ───────────────────────
  useEffect(() => {
    if (!apiClient) {
      setIsLoading(false)
      return
    }
    let cancelled = false
    const url = organisationId
      ? `/settings/navigation/?organisation_id=${organisationId}`
      : '/settings/navigation/'
    apiClient
      .get(url)
      .then((response: unknown) => {
        if (cancelled) return
        const payload = (response && typeof response === 'object' && 'data' in response
          ? (response as { data?: unknown }).data
          : response) as { preferences?: Record<string, unknown> } | undefined
        const prefs = payload?.preferences ?? {}
        const remotePinnedPages = Array.isArray(prefs['pinned_pages'])
          ? (prefs['pinned_pages'] as PinnedNavItem[])
          : []
        const remoteNavItemOrder =
          prefs['nav_item_order'] && typeof prefs['nav_item_order'] === 'object'
            ? (prefs['nav_item_order'] as Record<string, string[]>)
            : {}
        const remoteHiddenIds = Array.isArray(prefs['hidden_pages'])
          ? (prefs['hidden_pages'] as unknown[]).filter(
              (v): v is string => typeof v === 'string',
            )
          : []
        // Respect an explicit cross-device preference. Only a boolean
        // `true` enables it; anything else (including a missing field)
        // keeps the opt-in default off.
        const remoteAiAutoOrder =
          typeof prefs['ai_auto_order'] === 'boolean'
            ? prefs['ai_auto_order'] === true
            : undefined
        setState((prev) => {
          const next: NavCustomizationState = {
            pinnedPages:
              remotePinnedPages.length > 0 ? remotePinnedPages : prev.pinnedPages,
            navItemOrder:
              Object.keys(remoteNavItemOrder).length > 0
                ? remoteNavItemOrder
                : prev.navItemOrder,
            // Empty remote ⇒ keep whatever the local blob already had so a
            // first-ever sync (BE default []) can't wipe a same-tab hide the
            // user just made before hydrate resolved.
            hiddenIds:
              remoteHiddenIds.length > 0 ? remoteHiddenIds : prev.hiddenIds,
            aiAutoOrder:
              remoteAiAutoOrder !== undefined ? remoteAiAutoOrder : prev.aiAutoOrder,
          }
          writeNavCustomization(next)
          return next
        })
      })
      .catch(() => {
        // Backend unreachable — happy path is localStorage already
        // loaded by the initial useState. No-op.
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [apiClient, organisationId])

  // ── Persist to localStorage + backend on every change ───
  const persist = useCallback(
    (next: NavCustomizationState) => {
      writeNavCustomization(next)
      if (!apiClient) return
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
      saveTimerRef.current = setTimeout(() => {
        const url = organisationId
          ? `/settings/navigation/?organisation_id=${organisationId}`
          : '/settings/navigation/'
        // Partial PUT — only the fields this hook owns. The BE silently
        // preserves other fields (verified by qb!2956's backward-compat
        // test).
        const body = {
          preferences: {
            pinned_pages: next.pinnedPages,
            nav_item_order: next.navItemOrder,
            hidden_pages: next.hiddenIds,
          },
        }
        apiClient.put(url, body).catch(() => {
          // Backend write failed — localStorage already up to date so
          // the user sees no regression on this device. A future
          // session will re-sync from whichever side has more recent
          // data. No toast: the user didn't ask for an explicit save.
        })
      }, SAVE_DEBOUNCE_MS)
    },
    [apiClient, organisationId],
  )

  useEffect(() => () => {
    // Cleanup any pending debounce on unmount so we don't fire a
    // stale request after the user navigates away.
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
  }, [])

  // ── Keep the `aiAutoOrder` flag in sync ──────────────────
  // The flag is OWNED by the settings UI (NavigationTab /
  // NavigationCustomization), not this hook. When the operator toggles
  // "Smart ordering" there, we must pick it up without a reload:
  //   - `storage` covers OTHER tabs (cross-tab sync), and
  //   - the custom `NAV_CUSTOMIZATION_CHANGED_EVENT` covers the SAME
  //     tab (browsers don't fire `storage` in the writing document).
  // We only patch `aiAutoOrder` (not the whole blob) so in-memory
  // pins / manual order stay authoritative.
  useEffect(() => {
    const syncFlag = () => {
      const fresh = readNavCustomization()
      setState((prev) =>
        prev.aiAutoOrder === fresh.aiAutoOrder
          ? prev
          : { ...prev, aiAutoOrder: fresh.aiAutoOrder },
      )
    }
    window.addEventListener('storage', syncFlag)
    window.addEventListener(NAV_CUSTOMIZATION_CHANGED_EVENT, syncFlag)
    return () => {
      window.removeEventListener('storage', syncFlag)
      window.removeEventListener(NAV_CUSTOMIZATION_CHANGED_EVENT, syncFlag)
    }
  }, [])

  // Resolve the gate: an explicit option wins; otherwise the stored
  // flag decides. Strict boolean — never accidentally truthy.
  const autoOrderEnabled =
    (autoOrderByUsage !== undefined ? autoOrderByUsage : state.aiAutoOrder) === true

  // Bound click recorder — the host passes the item id, we own the hub
  // scope. Bumping the epoch re-runs the ordering memo so the rail
  // re-sorts immediately after the click.
  const trackNavClick = useCallback(
    (itemId: string) => {
      if (!itemId) return
      recordNavClick(itemId, hubId as HubId)
      setUsageEpoch((n) => n + 1)
    },
    [hubId],
  )

  // ── Derived: which items are pinned ──────────────────────
  const pinnedIds = useMemo(
    () => new Set(state.pinnedPages.map((p) => p.id)),
    [state.pinnedPages],
  )

  // ── Derived: which items are hidden ──────────────────────
  const hiddenIdSet = useMemo(() => new Set(state.hiddenIds), [state.hiddenIds])

  // Add / remove an id from the hidden list and persist. Shared by the
  // right-click "Hide / Show" rows and the restore tray's unhide button.
  const setItemHidden = useCallback(
    (itemId: string, hide: boolean) => {
      setState((prev) => {
        const nextHidden = toggleHidden(prev.hiddenIds, itemId, hide)
        // toggleHidden always returns a fresh array; only persist when the
        // CONTENTS actually changed (hiding an already-hidden item, or
        // showing a non-hidden one, is a no-op we don't want to PUT).
        const unchanged =
          nextHidden.length === prev.hiddenIds.length &&
          nextHidden.every((id, i) => id === prev.hiddenIds[i])
        if (unchanged) return prev
        const next: NavCustomizationState = { ...prev, hiddenIds: nextHidden }
        persist(next)
        return next
      })
    },
    [persist],
  )

  const unhideItem = useCallback(
    (itemId: string) => setItemHidden(itemId, false),
    [setItemHidden],
  )

  // ── Derived: ordered nav items for this hub ──────────────
  // Manual Move-Up/Down order is the base; when the operator has opted
  // into Smart ordering the usage engine re-sorts on top. `usageEpoch`
  // forces a recompute after each tracked click.
  const orderedAllNavItems = useMemo(
    () =>
      resolveOrderedNavItems(
        navItems,
        state.navItemOrder[hubId],
        hubId,
        autoOrderEnabled,
        { preserveFirst: preserveFirstCount, strategy: navOrderStrategy },
      ),
    // usageEpoch is intentionally a dep: it changes the engine's hidden
    // localStorage input, which the memo can't otherwise observe.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      navItems, state.navItemOrder, hubId, autoOrderEnabled, usageEpoch,
      navOrderStrategy, preserveFirstCount,
    ],
  )

  // Split the ordered list into what the user sees (rail) vs what they've
  // hidden (restore tray). Hidden items are removed AFTER ordering so the
  // smart-order engine is unaffected by a hide.
  const { visible: orderedNavItems, hidden: hiddenItems } = useMemo(
    () => partitionHidden(orderedAllNavItems, state.hiddenIds),
    [orderedAllNavItems, state.hiddenIds],
  )

  // ── Pinned items (PinnedNavItem[]) for the sidebar ───────
  // RBAC dedupe: only surface pins whose underlying nav item is still
  // visible in the user's current navItems list. If a user is
  // demoted out of "billing", their pinned Billing page should not
  // continue to render — the dedupe happens here so the sidebar
  // never sees a pin it can't render.
  const pinnedItems = useMemo(() => {
    if (state.pinnedPages.length === 0) return [] as PinnedNavItem[]
    const visibleIds = new Set(navItems.map((n) => n.id))
    const hiddenSet = new Set(state.hiddenIds)
    // A hidden item disappears EVERYWHERE — including its pinned copy — so
    // "Hide" reads as one decisive action rather than leaving a ghost pin.
    return state.pinnedPages.filter(
      (p) => visibleIds.has(p.id) && !hiddenSet.has(p.id),
    )
  }, [state.pinnedPages, navItems, state.hiddenIds])

  // ── The resolver — one big closure, but pure I→O ─────────
  const resolveNavContext = useCallback<NavContextResolver>(
    (item: NavItem, section: NavSection): NavContextResolution => {
      const isPinned = pinnedIds.has(item.id)
      const order = state.navItemOrder[hubId] ?? orderedNavItems.map((n) => n.id)
      const idx = order.indexOf(item.id)

      // Bounds depend on the section. Pinned section has its own
      // ordering (pinnedPages[*].order); main / secondary use
      // `navItemOrder[hubId]`. For now we expose Move-Up/Down on
      // main + secondary only — pinned reorder is wired but the
      // pinnedPages[].order field is owned by the existing
      // NavigationCustomization modal.
      const canMove = section !== 'pinned'
      const canMoveUp = canMove && idx > 0
      const canMoveDown = canMove && idx >= 0 && idx < order.length - 1

      const onPin = () => {
        // Build a PinnedNavItem from the NavItem on the fly. The
        // `sourceHub` field is the current hub; href comes verbatim
        // from the nav item (caller is responsible for passing absolute
        // hrefs for cross-hub items, relative for in-hub).
        const newPin: PinnedNavItem = {
          id: item.id,
          sourceHub: hubId as PinnedNavItem['sourceHub'],
          label: item.label,
          href: item.href ?? '#',
          icon: item.icon,
          order: state.pinnedPages.length,
        }
        setState((prev) => {
          if (prev.pinnedPages.some((p) => p.id === item.id)) return prev
          const next: NavCustomizationState = {
            ...prev,
            pinnedPages: [...prev.pinnedPages, newPin],
          }
          persist(next)
          return next
        })
      }

      const onUnpin = () => {
        setState((prev) => {
          if (!prev.pinnedPages.some((p) => p.id === item.id)) return prev
          const next: NavCustomizationState = {
            ...prev,
            pinnedPages: prev.pinnedPages.filter((p) => p.id !== item.id),
          }
          persist(next)
          return next
        })
      }

      const onMoveUp = () => {
        if (!canMoveUp) return
        setState((prev) => {
          const currentOrder =
            prev.navItemOrder[hubId] ?? navItems.map((n) => n.id)
          const nextOrder = reorderItem(currentOrder, item.id, 'up')
          if (nextOrder === currentOrder) return prev
          const next: NavCustomizationState = {
            ...prev,
            navItemOrder: { ...prev.navItemOrder, [hubId]: nextOrder },
          }
          persist(next)
          return next
        })
      }

      const onMoveDown = () => {
        if (!canMoveDown) return
        setState((prev) => {
          const currentOrder =
            prev.navItemOrder[hubId] ?? navItems.map((n) => n.id)
          const nextOrder = reorderItem(currentOrder, item.id, 'down')
          if (nextOrder === currentOrder) return prev
          const next: NavCustomizationState = {
            ...prev,
            navItemOrder: { ...prev.navItemOrder, [hubId]: nextOrder },
          }
          persist(next)
          return next
        })
      }

      const onOpenInNewTab = item.href
        ? () => window.open(item.href!, '_blank', 'noopener,noreferrer')
        : undefined

      const isHidden = hiddenIdSet.has(item.id)

      return {
        isPinned,
        isHidden,
        canMoveUp,
        canMoveDown,
        actions: {
          onPin: isPinned ? undefined : onPin,
          onUnpin: isPinned ? onUnpin : undefined,
          onMoveUp: canMove ? onMoveUp : undefined,
          onMoveDown: canMove ? onMoveDown : undefined,
          // Hide / Show are mutually exclusive on a given item; the menu
          // builder picks the right row from `isHidden`.
          onHide: isHidden ? undefined : () => setItemHidden(item.id, true),
          onUnhide: isHidden ? () => setItemHidden(item.id, false) : undefined,
          onOpenCustomize,
          onOpenInNewTab,
        },
      }
    },
    [
      pinnedIds,
      hiddenIdSet,
      state.navItemOrder,
      state.pinnedPages,
      hubId,
      navItems,
      orderedNavItems,
      persist,
      setItemHidden,
      onOpenCustomize,
    ],
  )

  return {
    pinnedItems,
    resolveNavContext,
    orderedNavItems,
    hiddenItems,
    unhideItem,
    trackNavClick,
    autoOrderEnabled,
    isLoading,
  }
}
